/* ==========================================================================
   JADUZAR INK — Medical questionnaire enhancement.

   The page already works without this file: the order reference and all 49
   approved questions sit in one native Shopify contact form with a real
   submit button. Everything here is layered on top of that same DOM — the
   seven-step flow, validation, the adult-only check, the media-consent
   branches and the structured plain-text summary that goes into
   contact[body].

   What changed in this phase
   --------------------------
   · English only. The EN/DE/NL switch and the duplicated interface copy are
     gone; the approved German and Dutch question wording is archived in
     handoff/medical-translations/questions-de-nl.md.
   · No booking gate. The questionnaire no longer unlocks anything, so it no
     longer writes a completion receipt and no longer offers a way back to a
     Bali product. It is completed once per order, after checkout.
   · Order-aware. A non-medical order reference is captured at the top of the
     form and travels with the submission, so a questionnaire can be matched
     to a Shopify order. The order confirmation may prefill it through
     ?order=… — an order number is not health information.

   Privacy rules this file obeys, deliberately:
     - answers live only in the form DOM, never in localStorage,
       sessionStorage, cookies, URLs or analytics;
     - nothing is logged to the console;
     - the only value that touches sessionStorage is the non-secret
       reference string, so it can be shown back on the success screen,
       and it is removed the moment it is read.
   ========================================================================== */

(function () {
  'use strict';

  var CONTROL_CHARS = new RegExp('[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F]', 'g');
  var STORE_KEY = 'ji-med-ref';
  var TOTAL_STEPS = 7;
  var MAX_ANSWER = 2000;
  var MAX_ORDER = 60;
  var MAX_BODY = 60000;

  /* Interface strings. Medical wording lives in the Liquid; this is only the
     chrome around it, so it stays here rather than in the Customizer. */
  var UI = {
    required: 'This question must be answered.',
    choose: 'Please choose an answer.',
    email: 'Please enter a valid email address.',
    date: 'Please enter your date of birth.',
    minor:
      'We are sorry — JADUZAR INK does not offer treatment under anesthesia to anyone under 18. Nothing has been sent. Please contact us at info@jaduzar.com if you would like to talk this through.',
    future: 'Please check your date of birth.',
    consent: 'Please confirm this to continue.',
    order: 'Please enter your order number, or write “no order yet”.',
    mediaChoice: 'Please choose whether you consent to being photographed or filmed.',
    mediaScope: 'Please select what JADUZAR INK may use, or choose that you do not consent.',
    summaryTitle: 'Please complete the following before continuing:',
    step: 'Step {n} of {t}',
    stepAnnounce: 'Step {n} of {t}: {name}',
    sending: 'Sending…',
    reviewNone: 'Not answered',
    notProvided: 'Not provided'
  };

  /* ---------------------------------------------------------------- utils */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function fmt(tpl, map) {
    return tpl.replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : m;
    });
  }

  /* Answers are copied into a plain-text e-mail. Strip control characters
     and angle brackets so nothing can be smuggled into that summary as
     markup, normalise line endings, and cap a runaway paste — without
     touching the shape of a legitimate answer. */
  function clean(value) {
    var out = String(value == null ? '' : value)
      .replace(/\r\n?/g, '\n')
      .replace(CONTROL_CHARS, '')
      .replace(/[<>]/g, '')
      .trim();
    if (out.length > MAX_ANSWER) out = out.slice(0, MAX_ANSWER) + ' [...truncated]';
    return out;
  }

  /* Multi-line answers stay readable in a plain-text mail. */
  function indent(value) {
    return value.replace(/\n/g, '\n      ');
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function makeReference(now) {
    var alphabet = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    var out = '';
    var i;
    try {
      var bytes = new Uint8Array(6);
      window.crypto.getRandomValues(bytes);
      for (i = 0; i < 6; i++) out += alphabet[bytes[i] % alphabet.length];
    } catch (e) {
      for (i = 0; i < 6; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return 'JI-MED-' + now.getFullYear() + pad(now.getMonth() + 1) + pad(now.getDate()) + '-' + out;
  }

  function stamp(now) {
    var offset = -now.getTimezoneOffset();
    var sign = offset >= 0 ? '+' : '-';
    var abs = Math.abs(offset);
    var zone = '';
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      zone = '';
    }
    return (
      now.getFullYear() +
      '-' + pad(now.getMonth() + 1) +
      '-' + pad(now.getDate()) +
      ' ' + pad(now.getHours()) +
      ':' + pad(now.getMinutes()) +
      ' (' + (zone ? zone + ', ' : '') + 'UTC' + sign + pad(Math.floor(abs / 60)) + ':' + pad(abs % 60) + ')'
    );
  }

  function ageOn(birth, today) {
    var age = today.getFullYear() - birth.getFullYear();
    var m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  }

  /* -------------------------------------------------------------- success */

  function finishSuccessScreen(page) {
    /* This function runs only inside the block Liquid renders behind
       form.posted_successfully?, so reaching it *is* Shopify confirming the
       submission. Nothing is unlocked by it: the questionnaire is no longer
       a gate, so no completion receipt is written anywhere. */
    var reference = takeStoredReference();
    if (reference) showReference(page, reference);
  }

  function takeStoredReference() {
    var ref = null;
    try {
      ref = window.sessionStorage.getItem(STORE_KEY);
      window.sessionStorage.removeItem(STORE_KEY);
    } catch (e) {
      ref = null;
    }
    return ref;
  }

  function showReference(page, ref) {
    var holder = $('[data-jimq-success-ref]', page);
    var value = $('[data-jimq-success-ref-value]', page);
    if (!holder || !value || !ref) return;
    value.textContent = ref;
    holder.hidden = false;
  }

  /* An order number is not health information, so the confirmation e-mail is
     allowed to prefill it. Nothing else is ever read from the URL, and the
     value is length-capped and stripped before it is used. */
  function prefillOrder(form) {
    var input = $('[data-jimq-order-input]', form);
    if (!input || input.value) return;
    var raw = '';
    try {
      raw = new URLSearchParams(window.location.search).get('order') || '';
    } catch (e) {
      raw = '';
    }
    if (!raw) return;
    var value = clean(raw).slice(0, MAX_ORDER);
    if (value) input.value = value;
  }

  /* ------------------------------------------------------------ the thing */

  function Questionnaire(page, form) {
    this.page = page;
    this.form = form;
    this.step = 1;
    this.submitting = false;
    this.steps = $$('[data-jimq-step]', form);
    this.live = $('[data-jimq-live]', form);
    this.summary = $('[data-jimq-summary]', form);
    this.reference = makeReference(new Date());
    this.sendLabel = '';
  }

  Questionnaire.prototype.t = function (key) {
    return UI[key];
  };

  Questionnaire.prototype.say = function (message) {
    if (!this.live) return;
    var live = this.live;
    live.textContent = '';
    window.setTimeout(function () {
      live.textContent = message;
    }, 60);
  };

  Questionnaire.prototype.start = function () {
    var self = this;

    this.form.classList.add('is-enhanced');
    /* Steps 2-7 are hidden, so the browser must not try to report native
       validation on a field it cannot scroll to. Validation is ours now. */
    this.form.noValidate = true;

    var progress = $('[data-jimq-progress]', this.form);
    if (progress) progress.hidden = false;

    var button = $('[data-jimq-submit]', this.form);
    if (button) this.sendLabel = button.textContent.trim();

    $$('[data-jimq-back]', this.form).forEach(function (b) {
      b.addEventListener('click', function () {
        self.go(self.step - 1);
      });
    });
    $$('[data-jimq-next]', this.form).forEach(function (b) {
      b.addEventListener('click', function () {
        if (self.validate(self.step)) self.go(self.step + 1);
      });
    });

    $$('[data-jimq-media-choice], [data-jimq-scope]', this.form).forEach(function (input) {
      input.addEventListener('change', function () {
        self.syncMedia();
      });
    });

    /* Give the media field its name now that the script is definitely
       running. Without JavaScript it stays nameless and unsubmitted, and the
       <noscript> radio group is the only media control in the document.

       Scoped to that one input on purpose: every question container also
       carries data-jimq-name, and a broader selector stamps a stray (inert,
       but wrong) name attribute onto all 49 of them. */
    $$('input[data-jimq-media-status]', this.form).forEach(function (field) {
      var wanted = field.getAttribute('data-jimq-name');
      if (wanted) field.setAttribute('name', wanted);
    });

    /* Clear a question's error the moment it is answered. */
    this.form.addEventListener('input', function (event) {
      self.clearError(event.target);
    });
    this.form.addEventListener('change', function (event) {
      self.clearError(event.target);
    });

    this.form.addEventListener('submit', function (event) {
      self.onSubmit(event);
    });

    var ref = $('[data-jimq-ref-display]', this.form);
    if (ref) ref.textContent = this.reference;

    prefillOrder(this.form);
    this.syncMedia();
    this.go(1, true);
  };

  /* ---------------------------------------------------------------- steps */

  Questionnaire.prototype.stepEl = function (n) {
    for (var i = 0; i < this.steps.length; i++) {
      if (Number(this.steps[i].getAttribute('data-jimq-step')) === n) return this.steps[i];
    }
    return null;
  };

  Questionnaire.prototype.go = function (n, quiet) {
    if (n < 1 || n > TOTAL_STEPS) return;
    this.step = n;

    this.steps.forEach(function (section) {
      section.hidden = Number(section.getAttribute('data-jimq-step')) !== n;
    });

    if (n === TOTAL_STEPS) this.buildReview();
    this.paint();

    var current = this.stepEl(n);
    var heading = current ? $('.jimq-h2', current) : null;

    /* Only a step the reader asked for takes focus. On first paint the
       caret stays at the top of the page, where it belongs — and where a
       Shopify error message can claim it. */
    if (heading && !quiet) {
      heading.setAttribute('tabindex', '-1');
      try {
        heading.focus({ preventScroll: true });
      } catch (e) {
        heading.focus();
      }
      this.page.scrollIntoView({ block: 'start' });
    }

    if (!quiet) {
      this.say(
        fmt(this.t('stepAnnounce'), {
          n: n,
          t: TOTAL_STEPS,
          name: heading ? heading.textContent.trim() : ''
        })
      );
    }
  };

  Questionnaire.prototype.paint = function () {
    var n = this.step;
    var count = $('[data-jimq-progress-count]', this.form);
    var name = $('[data-jimq-progress-name]', this.form);
    var bar = $('[data-jimq-progress-bar]', this.form);
    var current = this.stepEl(n);
    var heading = current ? $('.jimq-h2', current) : null;

    if (count) count.textContent = fmt(this.t('step'), { n: n, t: TOTAL_STEPS });
    if (name) name.textContent = heading ? heading.textContent.trim() : '';
    if (bar) bar.style.width = (n / TOTAL_STEPS) * 100 + '%';

    $$('[data-jimq-back]', this.form).forEach(function (b) {
      b.hidden = n === 1;
    });
    $$('[data-jimq-next]', this.form).forEach(function (b) {
      b.hidden = n === TOTAL_STEPS;
    });
    $$('[data-jimq-submit]', this.form).forEach(function (b) {
      b.hidden = n !== TOTAL_STEPS;
    });
  };

  /* ---------------------------------------------------------- media logic */

  Questionnaire.prototype.mediaChoice = function () {
    var picked = $$('[data-jimq-media-choice]', this.form).filter(function (i) {
      return i.checked;
    })[0];
    return picked ? picked.value : '';
  };

  Questionnaire.prototype.syncMedia = function () {
    var scopes = $('[data-jimq-scopes]', this.form);
    var flag = $('[data-jimq-media-flag]', this.form);
    var choice = this.mediaChoice();
    var consented = choice === 'CONSENT GIVEN';

    if (scopes) {
      scopes.hidden = !consented;
      scopes.classList.toggle('is-locked', !consented);
      $$('[data-jimq-scope]', scopes).forEach(function (box) {
        /* Refusing sets every publication scope to No and takes the
           controls out of play entirely. */
        if (!consented) box.checked = false;
        box.disabled = !consented;
      });
    }
    if (flag) flag.hidden = choice !== 'DO NOT PHOTOGRAPH OR FILM';

    /* Collapse the two-part interface into the single final status that
       actually reaches Shopify. Blank until the choice is complete, so an
       unfinished answer can never be mistaken for a decision. */
    var field = $('[data-jimq-media-status]', this.form);
    if (field) field.value = this.mediaComplete() ? this.mediaStatus() : '';
  };

  /* True once the visitor has made a choice that is expressible as one of the
     four final statuses: refusal, or consent with at least one scope. */
  Questionnaire.prototype.mediaComplete = function () {
    var choice = this.mediaChoice();
    if (choice === 'DO NOT PHOTOGRAPH OR FILM') return true;
    if (choice !== 'CONSENT GIVEN') return false;
    return $$('[data-jimq-scope]', this.form).some(function (b) {
      return b.checked;
    });
  };

  /* One of exactly four values, the same four the no-JavaScript radio group
     offers. There is no fifth outcome and no contradictory pair. */
  Questionnaire.prototype.mediaStatus = function () {
    if (this.mediaChoice() !== 'CONSENT GIVEN') return 'DO NOT PHOTOGRAPH OR FILM';
    var scopes = $$('[data-jimq-scope]', this.form);
    var web = !!(scopes[0] && scopes[0].checked);
    var social = !!(scopes[1] && scopes[1].checked);
    if (web && social) return 'CONSENT GIVEN — WEBSITE + SOCIAL MEDIA / ADVERTISING';
    if (social) return 'CONSENT GIVEN — SOCIAL MEDIA / ADVERTISING ONLY';
    return 'CONSENT GIVEN — WEBSITE ONLY';
  };

  /* ----------------------------------------------------------- validation */

  Questionnaire.prototype.fail = function (block, key, target) {
    block.classList.add('is-invalid');
    block.setAttribute('data-jimq-errkey', key);
    var slot = $('[data-jimq-err]', block);
    if (slot) slot.textContent = this.t(key);
    if (target) target.setAttribute('aria-invalid', 'true');
  };

  Questionnaire.prototype.pass = function (block) {
    block.classList.remove('is-invalid');
    block.removeAttribute('data-jimq-errkey');
    var slot = $('[data-jimq-err]', block);
    if (slot) slot.textContent = '';
    $$('input, textarea', block).forEach(function (el) {
      el.removeAttribute('aria-invalid');
    });
  };

  Questionnaire.prototype.clearError = function (target) {
    if (!target || !target.closest) return;
    var block = target.closest('.jimq, .jimq-consent');
    if (block && block.classList.contains('is-invalid')) this.pass(block);
  };

  Questionnaire.prototype.invalidBlocks = function () {
    return $$('.is-invalid[data-jimq-errkey]', this.form);
  };

  /* Returns the blocks that failed, in document order. */
  Questionnaire.prototype.check = function (scope) {
    var self = this;
    var bad = [];
    var today = new Date();

    /* The order reference. Not a medical question and not numbered — the 49
       approved questions keep their own numbering untouched. */
    var order = $('[data-jimq-order]', scope);
    if (order) {
      var orderField = $('[data-jimq-order-input]', order);
      if (orderField && orderField.value.trim()) {
        self.pass(order);
      } else {
        self.fail(order, 'order', orderField);
        bad.push(order);
      }
    }

    $$('[data-jimq-q]', scope).forEach(function (block) {
      var type = block.getAttribute('data-jimq-type');
      var n = Number(block.getAttribute('data-jimq-q'));

      if (type === 'yesno' || type === 'sex') {
        var picked = $$('input[type="radio"]', block).some(function (i) {
          return i.checked;
        });
        if (picked) {
          self.pass(block);
        } else {
          self.fail(block, 'choose');
          bad.push(block);
        }
        return;
      }

      var field = $('input, textarea', block);
      if (!field) return;
      var value = field.value.trim();

      /* Questions 24 and 48 may be left empty. No error, no asterisk, and
         nothing to type in place of an answer — no N/A, no NVT, no dot. */
      if (!value && block.hasAttribute('data-jimq-optional')) {
        self.pass(block);
        return;
      }
      if (!value) {
        self.fail(block, type === 'date' ? 'date' : 'required', field);
        bad.push(block);
        return;
      }
      if (type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
        self.fail(block, 'email', field);
        bad.push(block);
        return;
      }
      if (n === 5) {
        var born = new Date(value + 'T00:00:00');
        if (isNaN(born.getTime()) || born > today) {
          self.fail(block, 'future', field);
          bad.push(block);
          return;
        }
        /* Adult-only eligibility. This blocks the submission outright: no
           partial questionnaire is sent and no guardian route is offered,
           because JADUZAR INK does not accept minors for this flow. */
        if (ageOn(born, today) < 18) {
          self.fail(block, 'minor', field);
          bad.push(block);
          return;
        }
      }
      self.pass(block);
    });

    /* Media consent: a choice is required, and consenting requires an
       explicit scope. Refusing requires nothing further. */
    var media = $('[data-jimq-media]', scope);
    if (media) {
      var choice = self.mediaChoice();
      var anyScope = $$('[data-jimq-scope]', media).some(function (b) {
        return b.checked;
      });
      if (!choice) {
        self.fail(media, 'mediaChoice');
        bad.push(media);
      } else if (choice === 'CONSENT GIVEN' && !anyScope) {
        self.fail(media, 'mediaScope');
        bad.push(media);
      } else {
        self.pass(media);
      }
    }

    $$('[data-jimq-required-check]', scope).forEach(function (box) {
      var block = box.closest('.jimq-consent');
      if (!block) return;
      if (box.checked) {
        self.pass(block);
      } else {
        self.fail(block, 'consent', box);
        bad.push(block);
      }
    });

    return bad;
  };

  Questionnaire.prototype.validate = function (stepNumber) {
    var scope = stepNumber ? this.stepEl(stepNumber) : this.form;
    if (!scope) return true;
    var bad = this.check(scope);
    this.showSummary(bad);
    if (bad.length) this.focusFirst(bad[0]);
    return bad.length === 0;
  };

  Questionnaire.prototype.blockLabel = function (block) {
    var label = $('.jimq__label, .jimq-consent__legend', block);
    return label ? label.textContent.replace(/\s+/g, ' ').trim() : '';
  };

  Questionnaire.prototype.showSummary = function (bad) {
    if (!this.summary) return;
    var list = $('[data-jimq-summary-list]', this.summary);
    var title = $('[data-jimq-summary-title]', this.summary);
    if (!list) return;

    while (list.firstChild) list.removeChild(list.firstChild);

    if (!bad.length) {
      this.summary.hidden = true;
      return;
    }
    if (title) title.textContent = this.t('summaryTitle');

    var self = this;
    bad.forEach(function (block) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#';
      a.textContent = self.blockLabel(block);
      a.addEventListener('click', function (event) {
        event.preventDefault();
        var section = block.closest('[data-jimq-step]');
        if (section) {
          var n = Number(section.getAttribute('data-jimq-step'));
          if (n !== self.step) self.go(n);
        }
        self.focusFirst(block);
      });
      li.appendChild(a);
      list.appendChild(li);
    });
    this.summary.hidden = false;
  };

  Questionnaire.prototype.focusFirst = function (block) {
    var target = $('input:not([disabled]), textarea', block);
    if (!target) return;
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      target.focus();
    }
    block.scrollIntoView({ block: 'center' });
  };

  /* --------------------------------------------------------------- review */

  Questionnaire.prototype.orderReference = function () {
    var field = $('[data-jimq-order-input]', this.form);
    return field ? clean(field.value).slice(0, MAX_ORDER) : '';
  };

  Questionnaire.prototype.answerOf = function (block) {
    var type = block.getAttribute('data-jimq-type');
    if (type === 'yesno' || type === 'sex') {
      var picked = $$('input[type="radio"]', block).filter(function (i) {
        return i.checked;
      })[0];
      if (!picked) return { value: '', display: '' };
      var span = picked.parentNode.querySelector('span');
      return { value: picked.value, display: span ? span.textContent.trim() : picked.value };
    }
    var field = $('input, textarea', block);
    var value = field ? clean(field.value) : '';
    if (!value && block.hasAttribute('data-jimq-optional')) {
      return { value: '(not provided)', display: this.t('notProvided'), optionalEmpty: true };
    }
    return { value: value, display: value };
  };

  Questionnaire.prototype.buildReview = function () {
    var host = $('[data-jimq-review]', this.form);
    if (!host) return;
    while (host.firstChild) host.removeChild(host.firstChild);
    var self = this;

    var row = function (list, term, value) {
      var wrap = document.createElement('div');
      wrap.className = 'jimq-review__row';
      var dt = document.createElement('dt');
      dt.className = 'jimq-review__q';
      dt.textContent = term;
      var dd = document.createElement('dd');
      dd.className = 'jimq-review__a';
      dd.textContent = value;
      wrap.appendChild(dt);
      wrap.appendChild(dd);
      list.appendChild(wrap);
    };

    this.steps.forEach(function (section) {
      var n = Number(section.getAttribute('data-jimq-step'));
      if (n === TOTAL_STEPS) return;

      var group = document.createElement('div');
      group.className = 'jimq-review__group';

      var heading = $('.jimq-h2', section);
      var plate = document.createElement('p');
      plate.className = 'jimq-plate';
      plate.textContent = heading ? heading.textContent.trim() : '';
      group.appendChild(plate);

      var list = document.createElement('dl');
      list.className = 'jimq-review__list';

      /* The order reference sits at the top of step 1 and is reviewed with
         it, but it is never numbered as a medical question. */
      var order = $('[data-jimq-order]', section);
      if (order) {
        var label = $('.jimq__text', order);
        row(
          list,
          label ? label.textContent.trim() : 'Order reference',
          self.orderReference() || self.t('reviewNone')
        );
      }

      $$('[data-jimq-q]', section).forEach(function (block) {
        var wrap = document.createElement('div');
        wrap.className = 'jimq-review__row';

        var dt = document.createElement('dt');
        dt.className = 'jimq-review__q';
        var num = document.createElement('b');
        num.textContent = $('.jimq__num', block).textContent;
        dt.appendChild(num);
        dt.appendChild(document.createTextNode($('.jimq__text', block).textContent));

        var dd = document.createElement('dd');
        dd.className = 'jimq-review__a';
        var answer = self.answerOf(block);
        dd.textContent = answer.display || self.t('reviewNone');

        wrap.appendChild(dt);
        wrap.appendChild(dd);
        list.appendChild(wrap);
      });

      if (n === 6) {
        row(list, 'Media', self.mediaComplete() ? self.mediaStatus() : self.t('reviewNone'));
      }

      if (list.children.length) {
        group.appendChild(list);
        host.appendChild(group);
      }
    });
  };

  /* ----------------------------------------------------------------- mail */

  Questionnaire.prototype.valueOfQuestion = function (n) {
    var block = $('[data-jimq-q="' + n + '"]', this.form);
    return block ? this.answerOf(block).value : '';
  };

  Questionnaire.prototype.scopeState = function (index) {
    var boxes = $$('[data-jimq-scope]', this.form);
    return boxes[index] && boxes[index].checked && !boxes[index].disabled ? 'Yes' : 'No';
  };

  Questionnaire.prototype.checkState = function (selector) {
    var box = $(selector + ' input[type="checkbox"]', this.form);
    return box && box.checked ? 'GIVEN' : 'NOT GIVEN';
  };

  Questionnaire.prototype.consentWording = function () {
    /* The sentence the visitor ticked, not the section legend above it. */
    var span = $('[data-jimq-processing] .jimq__opt span', this.form);
    return span ? span.textContent : '';
  };

  Questionnaire.prototype.consentVersion = function () {
    var field = $('input[name="contact[Consent version]"]', this.form);
    return field ? field.value : '';
  };

  /* The mail is what JADUZAR INK reads and what the internal document
     builder parses, so its shape matters: a summary block that answers
     "who, which order, is this urgent" in the first screen, then the
     sections, then every question as a numbered Qnn / Ann pair. */
  Questionnaire.prototype.buildBody = function () {
    var self = this;
    var now = new Date();
    var line = '------------------------------------------------------------';
    var rule = '============================================================';
    var out = [];

    var full = (this.valueOfQuestion(2) + ' ' + this.valueOfQuestion(4)).replace(/\s+/g, ' ').trim();
    var dob = this.valueOfQuestion(5);
    var age = '';
    if (dob) {
      var born = new Date(dob + 'T00:00:00');
      if (!isNaN(born.getTime())) age = String(ageOn(born, now));
    }

    /* Identification lives in the body, not in the subject line: Shopify's
       contact notification does not necessarily carry contact[subject]
       through to the real e-mail subject, so the first line of the body is
       the flag JADUZAR INK filters and searches on. contact[subject] is
       still sent as well, but nothing depends on it. */
    out.push('[MEDICAL REVIEW REQUIRED]');
    out.push('JADUZAR INK — MEDICAL QUESTIONNAIRE');
    out.push('AWAITING JADUZAR INK REVIEW — THIS IS NOT MEDICAL APPROVAL');
    out.push('');
    out.push('SUMMARY');
    out.push('  Reference               ' + this.reference);
    out.push('  Order reference         ' + (this.orderReference() || '(not given)'));
    out.push('  Submitted               ' + stamp(now));
    out.push('  Client                  ' + full);
    out.push('  Date of birth           ' + dob + (age ? '   (age ' + age + ' — 18 or over: YES)' : ''));
    out.push('  Email                   ' + this.valueOfQuestion(6));
    out.push('  Phone                   ' + this.valueOfQuestion(7));
    out.push('');
    out.push(rule);
    out.push('MEDIA STATUS: ' + this.mediaStatus());
    out.push(rule);
    /* Two spaces of indent, then at least two before the value: that is the
       shape the internal document builder parses a fact line by. Keep the
       padding — a single space silently drops the line from the document. */
    out.push('  Website permission                      ' + this.scopeState(0));
    out.push('  Social media / advertising permission   ' + this.scopeState(1));
    out.push('Media consent is separate from medical screening and has no effect');
    out.push('on booking, treatment or medical review. It can be withdrawn at any');
    out.push('time by writing to info@jaduzar.com.');
    out.push('');
    out.push('CONSENT');
    out.push(line);
    out.push('  Processing and forwarding consent     ' + this.checkState('[data-jimq-processing]'));
    out.push('  Privacy acknowledgement               ' + this.checkState('[data-jimq-privacy]'));
    out.push('  Consent recorded at                   ' + stamp(now));
    out.push('  Consent wording version               ' + clean(this.consentVersion()));
    out.push('');
    out.push('Wording agreed to:');
    out.push('  "' + clean(this.consentWording()) + '"');
    out.push('');

    /* Each step becomes a titled section, and each question a Qnn / Ann
       pair, in the approved order. */
    this.steps.forEach(function (section) {
      var blocks = $$('[data-jimq-q]', section);
      if (!blocks.length) return;
      var heading = $('.jimq-h2', section);
      out.push('');
      out.push('--- ' + (heading ? heading.textContent.trim().toUpperCase() : 'QUESTIONS') + ' ---');
      out.push('');
      blocks.forEach(function (block) {
        var num = $('.jimq__num', block).textContent.trim();
        var text = $('.jimq__text', block);
        var english = (text && (text.getAttribute('data-en') || text.textContent)) || '';
        var answer = self.answerOf(block);
        out.push('Q' + num + '  ' + clean(english));
        out.push('A' + num + '  ' + indent(answer.value || '(no answer)'));
        out.push('');
      });
    });

    out.push('--- END OF QUESTIONNAIRE ---');
    out.push('');
    out.push('STATUS');
    out.push(line);
    out.push('This questionnaire is awaiting JADUZAR INK review. It is not medical');
    out.push('approval. The responsible medical professional makes the final');
    out.push('medical decision.');

    var body = out.join('\n');
    if (body.length > MAX_BODY) body = body.slice(0, MAX_BODY) + '\n[...truncated]';
    return body;
  };

  /* --------------------------------------------------------------- submit */

  Questionnaire.prototype.set = function (selector, value) {
    var field = $(selector, this.form);
    if (field) field.value = value;
  };

  Questionnaire.prototype.stripNames = function () {
    $$('[data-jimq-field][name]', this.form).forEach(function (field) {
      field.setAttribute('data-jimq-oldname', field.getAttribute('name'));
      field.removeAttribute('name');
    });
  };

  Questionnaire.prototype.restore = function () {
    if (!this.submitting) return;
    this.submitting = false;
    $$('[data-jimq-oldname]', this.form).forEach(function (field) {
      field.setAttribute('name', field.getAttribute('data-jimq-oldname'));
      field.removeAttribute('data-jimq-oldname');
    });
    var button = $('[data-jimq-submit]', this.form);
    if (button) {
      button.removeAttribute('aria-disabled');
      if (this.sendLabel) button.textContent = this.sendLabel;
    }
  };

  Questionnaire.prototype.onSubmit = function (event) {
    /* A second click while the first submission is in flight does nothing. */
    if (this.submitting) {
      event.preventDefault();
      return;
    }

    var bad = this.check(this.form);
    if (bad.length) {
      event.preventDefault();
      this.showSummary(bad);
      var section = bad[0].closest('[data-jimq-step]');
      if (section) {
        var n = Number(section.getAttribute('data-jimq-step'));
        if (n !== this.step) this.go(n);
      }
      this.focusFirst(bad[0]);
      return;
    }
    this.showSummary([]);

    var body = '';
    try {
      body = this.buildBody();
    } catch (e) {
      /* If the summary cannot be built, let the submission go ahead with
         every answer still present as its own named field rather than
         losing the questionnaire. */
      body = '';
    }

    var full = (this.valueOfQuestion(2) + ' ' + this.valueOfQuestion(4)).replace(/\s+/g, ' ').trim();
    var order = this.orderReference();
    this.set('[data-jimq-hidden-name]', full);
    this.set(
      '[data-jimq-hidden-subject]',
      '[MEDICAL REVIEW REQUIRED] JADUZAR INK medical questionnaire — ' +
        this.reference +
        (order ? ' — order ' + order : '') +
        (full ? ' — ' + full : '')
    );
    this.set('[data-jimq-hidden-ref]', this.reference);
    this.set('[data-jimq-hidden-order]', order);
    this.set('[data-jimq-hidden-time]', stamp(new Date()));
    this.set('[data-jimq-hidden-age]', 'Yes');

    if (body) {
      this.set('[data-jimq-hidden-body]', body);
      /* The summary now holds every answer in order, so the per-question
         parameters are dropped from the POST: one clean, readable mail
         instead of the same content twice. Questions 6 and 7 keep their
         names, because contact[email] is the one field Shopify requires. */
      this.stripNames();
    }

    this.submitting = true;
    var button = $('[data-jimq-submit]', this.form);
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.textContent = this.t('sending');
    }

    try {
      /* The reference is not health information and not a secret. It is
         held only long enough to show back on the success screen, and is
         removed the moment it is read. */
      window.sessionStorage.setItem(STORE_KEY, this.reference);
    } catch (e) {
      /* Private browsing. The reference is still in the mail. */
    }

    /* If the navigation never happens — offline, a blocked request — put
       the form back exactly as it was, so the answers on screen are not
       stranded behind a dead button. */
    var self = this;
    window.setTimeout(function () {
      self.restore();
    }, 12000);
  };

  /* ----------------------------------------------------------------- boot */

  function boot() {
    var page = $('[data-jimq-page]');
    if (!page) return;

    if ($('[data-jimq-success]', page)) {
      finishSuccessScreen(page);
      return;
    }

    var form = $('form.jimq-form', page);
    if (!form) return;

    new Questionnaire(page, form).start();

    /* Shopify rejected the last POST: send the reader straight to why. */
    var failure = $('[data-jimq-shopify-error]', page);
    if (failure) failure.focus();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
